import { useState, useCallback, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  ArrowLeft, Clock, Stethoscope, FileCheck, Sparkles, Plus,
  Loader2, AlertTriangle, CalendarOff, CheckCircle2,
} from 'lucide-react';
import { PostOpNoteStep } from '@/components/surgeon/v2/PostOpNoteStep';
import { SoloSurgeonDropzone } from '@/components/surgeon/v2/SoloSurgeonDropzone';
import { SoloDailyQueueList } from '@/components/surgeon/v2/SoloDailyQueueList';
import { StaffPreChartForm } from '@/components/surgeon/v2/StaffPreChartForm';
import { useSoloDailyQueue } from '@/hooks/useSoloDailyQueue';
import { ScheduledCase, SurgeonTemplate, CaseCptLine, SurgeonFacility, Laterality } from '@/lib/templates/types';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { getOrthopedicDemoPostOpCases } from '@/lib/demo/orthopedicPatientPackets';
import { getFinalizedAuthorizationBundle } from '@/lib/surgeon-console/finalizedAuthorization';
import {
  canonicalDemoCaseId,
  getDemoPresentationState,
  resetDemoPatientState,
  subscribeDemoPresentationState,
} from '@/lib/demo/demoPresentationState';

// ── Live OR queue row shape (derived from authorization_requests) ───────────
interface ORPatient {
  id: string;                          // authorization_requests.id
  patient_id: string;
  patient_name: string;
  patient_mrn: string;
  patient_dob: string;
  patient_sex?: string | null;
  scheduled_time: string;              // formatted from scheduled_date
  scheduled_date: string;              // raw ISO date
  authorized_procedure: string;        // procedure_description
  authorized_cpt: string;              // primary CPT
  modifier: string | null;             // primary modifier (RT/LT/T6/etc.)
  laterality: Laterality | null;
  facility_name: string;
  payer_name: string | null;
  diagnosis_code: string | null;
  diagnosis_summary: string | null;
  encounter_id: string | null;
  body_region: string;
  status: 'pending_dictation' | 'note_signed';
  cpt_bundle: Array<{ code: string; modifier?: string; description?: string; units?: number; wound_size_sq_cm?: number }>;
  clinical_metrics: Record<string, unknown>;
  diagnosis_codes?: Array<{ code: string; description: string }>;
}

interface AuthorizationQueueRow {
  id: string;
  patient_id: string;
  case_number: string | null;
  status: string | null;
  scheduled_date: string | null;
  cpt_codes: string[] | null;
  modifiers: string[] | null;
  icd10_codes: string[] | null;
  diagnosis_summary: string | null;
  procedure_description: string | null;
  cpt_bundle: ORPatient['cpt_bundle'] | null;
  clinical_metrics: Record<string, unknown> | null;
  epic_encounter_id: string | null;
  patients: {
    mrn: string | null;
    first_name: string | null;
    last_name: string | null;
    dob: string | null;
  } | null;
  payers: {
    name: string | null;
  } | null;
}

const PRIMARY_LATERAL_MODS = new Set(['RT', 'LT']);

function modifierToLaterality(mod: string | null | undefined): Laterality | null {
  if (mod === 'RT') return 'right';
  if (mod === 'LT') return 'left';
  return null;
}

function inferBodyRegion(cpt: string): string {
  const n = parseInt(cpt, 10);
  if (cpt.startsWith('Q')) return 'Wound Care';
  if (Number.isNaN(n)) return 'General';
  if (n >= 28000 && n < 29000) return 'Foot';
  if (n >= 27600 && n < 28000) return 'Ankle / Lower Leg';
  if (n >= 27000 && n < 27600) return 'Hip / Femur / Knee';
  if (n >= 29800 && n < 30000) return 'Arthroscopy';
  if (n === 15002 || n === 15003) return 'Wound Bed Prep';
  return 'General';
}

function formatScheduledTime(scheduledDate: string | null): string {
  if (!scheduledDate) return '—';
  return new Date(scheduledDate).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
}

function buildCptLinesFromBundle(
  bundle: ORPatient['cpt_bundle'],
  procedureDescription: string,
): CaseCptLine[] {
  if (!bundle || bundle.length === 0) {
    return [];
  }
  return bundle.map((entry, idx) => ({
    code: entry.code,
    description: entry.description || (idx === 0 ? procedureDescription : entry.code),
    modifiers: entry.modifier ? [entry.modifier] : [],
    section: idx === 0 ? ('primary' as const) : ('secondary' as const),
    fromTemplate: true,
  }));
}

function buildDemoORPatients(): ORPatient[] {
  return getOrthopedicDemoPostOpCases().map((demoCase): ORPatient => {
    const signedBundle = getFinalizedAuthorizationBundle(demoCase.scheduledCase.id);
    const finalCptLines = signedBundle?.cptLines || demoCase.cptLines;
    const primaryLine = finalCptLines[0];
    const scheduledDate = demoCase.scheduledCase.scheduled_date || new Date().toISOString();
    const diagnosis = signedBundle?.diagnosisCodes.find(item => item.authorizationLinked) || demoCase.template.diagnosis_codes[0] || null;

    return {
      id: `demo-post-op-${demoCase.scheduledCase.id}`,
      patient_id: demoCase.scheduledCase.id,
      patient_name: demoCase.scheduledCase.patient_name,
      patient_mrn: demoCase.scheduledCase.patient_mrn || '—',
      patient_dob: demoCase.scheduledCase.patient_dob || '',
      patient_sex: demoCase.scheduledCase.patient_sex,
      scheduled_time: formatScheduledTime(scheduledDate),
      scheduled_date: scheduledDate,
      authorized_procedure: signedBundle?.procedureName || demoCase.template.template_name,
      authorized_cpt: primaryLine?.code || demoCase.template.primary_cpts[0]?.code || '—',
      modifier: primaryLine?.modifiers[0] || null,
      laterality: signedBundle?.laterality || demoCase.laterality,
      facility_name: signedBundle?.facilityName || demoCase.scheduledCase.facility_name || 'St. Elizabeth Florence',
      payer_name: demoCase.scheduledCase.payer_name,
      diagnosis_code: diagnosis?.code || null,
      diagnosis_summary: diagnosis?.description || null,
      encounter_id: demoCase.scheduledCase.encounter_id,
      body_region: demoCase.template.body_region || inferBodyRegion(primaryLine?.code || ''),
      status: 'pending_dictation',
      cpt_bundle: finalCptLines.map((line) => ({
        code: line.code,
        modifier: line.modifiers[0],
        description: line.description,
      })),
      clinical_metrics: demoCase.clinicalMetrics,
      diagnosis_codes: signedBundle?.diagnosisCodes
        .filter(item => item.authorizationLinked)
        .map(item => ({ code: item.code, description: item.description })),
    };
  });
}

function buildMockDataForPatient(patient: ORPatient): {
  selectedCase: ScheduledCase;
  template: SurgeonTemplate;
  facility: SurgeonFacility;
  cptLines: CaseCptLine[];
} {
  const cptLines = buildCptLinesFromBundle(patient.cpt_bundle, patient.authorized_procedure);

  return {
    selectedCase: {
      id: patient.id,
      patient_name: patient.patient_name,
      patient_dob: patient.patient_dob,
      patient_mrn: patient.patient_mrn,
      surgeon_id: 'live-surgeon',
      facility_id: null,
      facility_name: patient.facility_name,
      scheduled_date: patient.scheduled_date,
      scheduled_procedure: patient.authorized_procedure,
      encounter_id: patient.encounter_id,
      payer_name: patient.payer_name,
      plan_name: null,
      member_id: null,
      patient_sex: patient.patient_sex || 'unknown',
      clinical_metrics: patient.clinical_metrics,
      status: 'scheduled',
      is_demo: patient.id.startsWith('demo-post-op-'),
    },
    template: {
      id: `live-template-${patient.id}`,
      surgeon_id: 'live-surgeon',
      template_name: patient.authorized_procedure,
      specialty: 'Orthopedic Surgery',
      body_region: patient.body_region,
      laterality_required: !!patient.laterality,
      primary_cpts: [{ code: patient.authorized_cpt, description: patient.authorized_procedure }],
      secondary_cpts: [],
      narrative_template: null,
      common_addons: [],
      diagnosis_codes: patient.diagnosis_codes?.length
        ? patient.diagnosis_codes
        : patient.diagnosis_code
          ? [{ code: patient.diagnosis_code, description: patient.diagnosis_summary || patient.diagnosis_code }]
          : [],
      metadata: {
        // Pass through rich payload so the post-op step can read wound_size, etc.
        cpt_bundle: patient.cpt_bundle,
        clinical_metrics: patient.clinical_metrics,
      },
      version: 1,
      is_active: true,
      is_demo: patient.id.startsWith('demo-post-op-'),
      created_at: '',
      updated_at: '',
    },
    facility: {
      id: 'live-fac',
      surgeon_id: 'live-surgeon',
      facility_name: patient.facility_name,
      facility_type: patient.id.startsWith('demo-post-op-') ? 'Hospital outpatient' : 'ASC',
      address: null,
      phone: null,
      is_default: true,
      is_active: true,
      is_demo: patient.id.startsWith('demo-post-op-'),
    },
    cptLines,
  };
}

function useDemoPresentationStateSnapshot() {
  const [state, setState] = useState(() => getDemoPresentationState());

  useEffect(() => subscribeDemoPresentationState(setState), []);

  return state;
}

// ── Live query hook ────────────────────────────────────────────────────────
async function fetchORQueue(): Promise<ORPatient[]> {
  // Pull cases that have cleared payer review and are queued for OR /
  // post-op reconciliation. RLS (auth_requests_secured_read) restricts this
  // to the active surgeon / client by design — no client-side filtering needed.
  const { data, error } = await supabase
    .from('authorization_requests')
    .select(`
      id,
      patient_id,
      case_number,
      status,
      scheduled_date,
      cpt_codes,
      modifiers,
      icd10_codes,
      diagnosis_summary,
      procedure_description,
      cpt_bundle,
      clinical_metrics,
      epic_encounter_id,
      patients ( mrn, first_name, last_name, dob ),
      payers ( name )
    `)
    .in('status', [
      'payer_approved',
      'awaiting_post_op_reconciliation',
      'surgeon_approved',
    ])
    .order('scheduled_date', { ascending: true });

  if (error) throw error;

  return ((data || []) as unknown as AuthorizationQueueRow[]).map((row): ORPatient => {
    const bundle: ORPatient['cpt_bundle'] = Array.isArray(row.cpt_bundle) ? row.cpt_bundle : [];
    const primaryEntry = bundle[0];
    const primaryCpt = primaryEntry?.code || row.cpt_codes?.[0] || '—';
    const primaryMod = primaryEntry?.modifier || row.modifiers?.[0] || null;
    const dxCode = row.icd10_codes?.[0] || null;
    const diagnosisCodes = (row.icd10_codes || []).map((code) => ({
      code,
      description: row.diagnosis_summary || code,
    }));

    const scheduledRaw = row.scheduled_date as string | null;
    const scheduledTime = scheduledRaw
      ? new Date(scheduledRaw).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
      : '—';

    return {
      id: row.id,
      patient_id: row.patient_id,
      patient_name: row.patients
        ? `${row.patients.first_name ?? ''} ${row.patients.last_name ?? ''}`.trim() || 'Unknown'
        : 'Unknown',
      patient_mrn: row.patients?.mrn || '—',
      patient_dob: row.patients?.dob || '',
      scheduled_time: scheduledTime,
      scheduled_date: scheduledRaw || new Date().toISOString().split('T')[0],
      authorized_procedure: row.procedure_description || primaryCpt,
      authorized_cpt: primaryCpt,
      modifier: primaryMod,
      laterality: modifierToLaterality(primaryMod) ??
        (PRIMARY_LATERAL_MODS.has(primaryMod ?? '') ? null : null),
      facility_name: 'Progressive Podiatry', // facility join not modeled here yet
      payer_name: row.payers?.name || null,
      diagnosis_code: dxCode,
      diagnosis_summary: row.diagnosis_summary || null,
      encounter_id: row.epic_encounter_id || row.case_number || null,
      body_region: inferBodyRegion(primaryCpt),
      status: row.status === 'surgeon_approved' ? 'note_signed' : 'pending_dictation',
      cpt_bundle: bundle,
      clinical_metrics: row.clinical_metrics || {},
      diagnosis_codes: diagnosisCodes,
    };
  });
}

export function TodaysORWorkspace() {
  const { isDemoMode } = useDemoMode();
  const [activePatient, setActivePatient] = useState<ORPatient | null>(null);
  const [dropzoneCase, setDropzoneCase] = useState<{
    selectedCase: ScheduledCase;
    template: SurgeonTemplate;
    laterality: Laterality | null;
    facility: SurgeonFacility;
    cptLines: CaseCptLine[];
  } | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const demoPresentationState = useDemoPresentationStateSnapshot();
  const highlightedCaseId = isDemoMode ? demoPresentationState.signedPreAuthCaseId : null;

  const { sortedPatients, addPatient, removePatient, clearQueue, patientCount } = useSoloDailyQueue();

  const {
    data: orPatients = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['or-queue', 'authorized', isDemoMode ? 'demo' : 'live'],
    queryFn: fetchORQueue,
    initialData: [],
    enabled: !isDemoMode,
    staleTime: 30_000,
  });

  const demoORPatients = useMemo(
    () => (isDemoMode ? buildDemoORPatients() : []),
    [isDemoMode, demoPresentationState.updatedAt],
  );
  const effectiveORPatients = useMemo(() => {
    const basePatients = isDemoMode ? demoORPatients : orPatients;
    if (!isDemoMode || !demoPresentationState.postOpSignedCaseId) return basePatients;

    return basePatients.map((patient) => (
      canonicalDemoCaseId(patient.patient_id) === demoPresentationState.postOpSignedCaseId
        ? { ...patient, status: 'note_signed' as const }
        : patient
    ));
  }, [demoORPatients, demoPresentationState.postOpSignedCaseId, isDemoMode, orPatients]);

  // Order: today first, then upcoming
  const sortedORPatients = useMemo(
    () => [...effectiveORPatients].sort((a, b) => {
      const aHighlighted = Boolean(highlightedCaseId && canonicalDemoCaseId(a.patient_id) === highlightedCaseId);
      const bHighlighted = Boolean(highlightedCaseId && canonicalDemoCaseId(b.patient_id) === highlightedCaseId);
      if (aHighlighted !== bHighlighted) return aHighlighted ? -1 : 1;
      return a.scheduled_date.localeCompare(b.scheduled_date);
    }),
    [effectiveORPatients, highlightedCaseId],
  );

  const handleReconcile = useCallback((patient: ORPatient) => {
    if (patient.status !== 'pending_dictation') return;
    setActivePatient(patient);
    setDropzoneCase(null);
  }, []);

  const handleDropzoneExtracted = useCallback((
    patient: ScheduledCase,
    autoMatchedTemplate?: SurgeonTemplate,
    extractedLaterality?: Laterality | null,
  ) => {
    const template: SurgeonTemplate = autoMatchedTemplate || {
      id: 'extracted-template',
      surgeon_id: 'demo-surgeon',
      template_name: patient.scheduled_procedure || 'Extracted Procedure',
      specialty: 'Orthopedic Surgery',
      body_region: 'General',
      laterality_required: !!extractedLaterality,
      primary_cpts: [],
      secondary_cpts: [],
      narrative_template: null,
      common_addons: [],
      diagnosis_codes: [],
      metadata: {},
      version: 1,
      is_active: true,
      is_demo: true,
      created_at: '',
      updated_at: '',
    };

    const facility: SurgeonFacility = {
      id: 'extracted-fac',
      surgeon_id: 'demo-surgeon',
      facility_name: patient.facility_name || 'Unknown Facility',
      facility_type: 'ASC',
      address: null,
      phone: null,
      is_default: true,
      is_active: true,
      is_demo: true,
    };

    const cptLines: CaseCptLine[] = template.primary_cpts.map(c => ({
      code: c.code,
      description: c.description,
      modifiers: extractedLaterality === 'right' ? ['RT'] : extractedLaterality === 'left' ? ['LT'] : [],
      section: 'primary' as const,
      fromTemplate: true,
    }));

    setDropzoneCase({ selectedCase: patient, template, laterality: extractedLaterality ?? null, facility, cptLines });
    setActivePatient(null);
  }, []);

  const handleBack = useCallback(() => {
    setActivePatient(null);
    setDropzoneCase(null);
  }, []);

  const activeCase = activePatient ? buildMockDataForPatient(activePatient) : dropzoneCase;
  const activeLaterality = activePatient ? activePatient.laterality : dropzoneCase?.laterality ?? null;

  if (activeCase) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={handleBack} className="gap-2">
          <ArrowLeft className="w-4 h-4" />
          Back to OR Schedule
        </Button>
        <PostOpNoteStep
          selectedCase={activeCase.selectedCase}
          template={activeCase.template}
          laterality={activeLaterality}
          facility={activeCase.facility}
          cptLines={activeCase.cptLines}
          onCaseDeferred={handleBack}
          onDemoResetCase={(caseId) => {
            resetDemoPatientState(caseId);
            setActivePatient(null);
            setDropzoneCase(null);
            toast.success('Demo case reset', {
              description: 'Pre-Auth and Post-Op state cleared for this patient.',
            });
          }}
        />
      </div>
    );
  }

  const today = new Date();
  const dateStr = today.toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });

  return (
    <div className="space-y-5">
      {/* ── SECTION 1: OR Schedule (live) ── */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Today's OR Schedule</h2>
          <p className="text-sm text-muted-foreground">{dateStr} · Authorized Surgical Cases</p>
        </div>
        <Badge variant="outline" className="gap-1.5 text-xs">
          <Clock className="w-3 h-3" />
          {isDemoMode ? 'Demo · Authorized Queue' : 'Live · Authorized Queue'}
        </Badge>
      </div>

      <div className="space-y-3">
        {isLoading && (
          <Card className="border-border/60">
            <CardContent className="p-8 flex flex-col items-center justify-center gap-3 text-center">
              <Loader2 className="w-6 h-6 text-primary animate-spin" />
              <p className="text-sm text-muted-foreground">Loading authorized surgical cases…</p>
            </CardContent>
          </Card>
        )}

        {isError && !isLoading && (
          <Card className="border-destructive/40 bg-destructive/5">
            <CardContent className="p-6 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
              <div className="flex-1 space-y-2">
                <p className="text-sm font-semibold text-destructive">
                  Could not load the OR queue
                </p>
                <p className="text-xs text-muted-foreground">
                  {(error as Error)?.message || 'Authorization service unreachable.'}
                </p>
                <Button size="sm" variant="outline" onClick={() => refetch()}>
                  Retry
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {!isLoading && !isError && sortedORPatients.length === 0 && (
          <Card className="border-dashed border-border/60">
            <CardContent className="p-10 flex flex-col items-center justify-center gap-3 text-center">
              <CalendarOff className="w-8 h-8 text-muted-foreground" />
              <div>
                <p className="text-sm font-semibold text-foreground">
                  No Active Surgical Cases
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Authorized cases will appear here once payer approval is received.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {!isLoading && !isError && sortedORPatients.map((patient) => {
          const isPending = patient.status === 'pending_dictation';
          const isHighlighted = Boolean(
            isDemoMode &&
            highlightedCaseId &&
            canonicalDemoCaseId(patient.patient_id) === highlightedCaseId,
          );
          return (
            <Card
              key={patient.id}
              className={
                isHighlighted
                  ? `border-emerald-300 bg-emerald-50/90 shadow-sm ring-1 ring-emerald-200 transition-colors ${isPending ? 'cursor-pointer hover:border-emerald-400 hover:bg-emerald-50' : 'hover:border-emerald-300'}`
                  : `border-border/60 transition-colors ${isPending ? 'cursor-pointer hover:border-primary/40 hover:bg-accent/40' : 'hover:border-border'}`
              }
              onClick={() => handleReconcile(patient)}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="text-center shrink-0 w-16">
                      <div className="text-sm font-bold text-foreground">{patient.scheduled_time}</div>
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm">{patient.patient_name}</span>
                        <span className="text-xs text-muted-foreground font-mono">{patient.patient_mrn}</span>
                        {isHighlighted && (
                          <Badge className="bg-emerald-600 text-white border-emerald-600 gap-1 text-[10px]">
                            <CheckCircle2 className="w-3 h-3" />
                            Ready for Post-Op
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <Stethoscope className="w-3 h-3 text-muted-foreground shrink-0" />
                        <span className="text-xs text-muted-foreground truncate">
                          {patient.authorized_cpt}
                          {patient.modifier ? `-${patient.modifier}` : ''}
                          {' · '}
                          {patient.authorized_procedure}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 shrink-0">
                    {patient.status === 'note_signed' ? (
                      <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/30 gap-1">
                        <FileCheck className="w-3 h-3" />
                        Note Signed
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="text-xs">
                        Pending Dictation
                      </Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* ── SECTION 2: Solo Surgeon Mode — Daily Queue ── */}
      <div className="flex items-center gap-3 pt-2">
        <Separator className="flex-1" />
        <span className="text-xs text-muted-foreground whitespace-nowrap">Solo Surgeon Mode — Daily Queue</span>
        <Separator className="flex-1" />
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-sky-50 border border-sky-200">
              <Sparkles className="w-4 h-4 text-sky-600" />
              <span className="text-sm font-semibold text-sky-900">Today's Solo Queue</span>
              {patientCount > 0 && (
                <Badge variant="outline" className="text-[10px] border-sky-300 text-sky-700 bg-white/60">
                  {patientCount} patient{patientCount !== 1 ? 's' : ''}
                </Badge>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {patientCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="text-[11px] text-muted-foreground hover:text-destructive"
                onClick={() => {
                  clearQueue();
                  toast.info('Solo queue cleared');
                }}
              >
                Clear Queue
              </Button>
            )}
            <Button
              variant={showAddForm ? 'secondary' : 'outline'}
              size="sm"
              className="text-[11px]"
              onClick={() => setShowAddForm(!showAddForm)}
            >
              {showAddForm ? (
                'Cancel'
              ) : (
                <>
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  Staff Pre-Charting
                </>
              )}
            </Button>
          </div>
        </div>

        {showAddForm && (
          <StaffPreChartForm
            onSave={(patient) => {
              addPatient(patient);
              setShowAddForm(false);
            }}
            onCancel={() => setShowAddForm(false)}
          />
        )}

        <SoloDailyQueueList
          patients={sortedPatients}
          onPatientClick={() => {}}
          onRemovePatient={removePatient}
          isStaffMode={false}
        />

        {patientCount === 0 && (
          <p className="text-sm text-muted-foreground text-center py-2">
            No patients in today's solo queue. Use "Staff Pre-Charting" to add patients.
          </p>
        )}

        <div className="flex items-center gap-3 pt-1">
          <Separator className="flex-1" />
          <span className="text-[11px] text-muted-foreground whitespace-nowrap">or upload a document directly</span>
          <Separator className="flex-1" />
        </div>

        <SoloSurgeonDropzone onPatientExtracted={(patient, template, laterality) => handleDropzoneExtracted(patient, template, laterality)} />
      </div>
    </div>
  );
}
